// The margin page's twelve months, from one month report and the eleven
// months before it.
//
// The page shows a month's margin (`/reports/margin`) and each customer's
// twelve months ending in that month (`/reports/margin-trend`). Asking the
// trend for the same twelve months makes the server work out the shown month
// twice, in parallel: and the shown month is usually the open one, whose
// statements are the expensive part of either report. So the trend is asked
// for the eleven months BEFORE the shown month only, and the shown month is
// added here from the month report the page already has.
//
// That is exact, not an estimate: the server builds both reports from the
// same per-month customer list (`marginMonth` in CrmMarginReportRoute.kt). A
// trend month's book point is that month's `totals`, and a customer's point is
// the customer's own four figures. A customer the earlier months never saw is
// added after the others with zeros for what it billed and nulls for what
// nobody can know, as `marginTrendCustomers` does on the server.

import type { MarginReport, MarginTrendPoint, MarginTrendReport } from "@crm/shared";
import { shiftPeriod } from "../close/closeSearch.js";

/** How many months the page draws, the shown month included. */
export const MARGIN_HISTORY_MONTHS = 12;

/** The months the trend is asked for: the eleven before `period`. */
export interface MarginHistoryWindow {
  fromMonth: string;
  toMonth: string;
}

export function marginHistoryWindow(
  period: string,
  months: number = MARGIN_HISTORY_MONTHS,
): MarginHistoryWindow {
  if (!Number.isInteger(months) || months < 2) {
    throw new RangeError(`A margin history needs at least two months, not ${months}`);
  }
  return { fromMonth: shiftPeriod(period, -(months - 1)), toMonth: shiftPeriod(period, -1) };
}

type MarginTrendCustomer = MarginTrendReport["customers"][number];
type MarginReportCustomer = MarginReport["customers"][number];

/** The month's book point, as the trend states it: the totals' four figures. */
export function bookPoint(report: MarginReport): MarginTrendPoint {
  return {
    period: report.params.period,
    recurring_our_share_minor: report.totals.recurring_our_share_minor,
    our_cost_minor: report.totals.our_cost_minor,
    contribution_minor: report.totals.contribution_minor,
    margin_pct: report.totals.margin_pct,
  };
}

/** One customer's point for the month, as the trend states it. */
export function customerPoint(period: string, customer: MarginReportCustomer): MarginTrendPoint {
  return {
    period,
    recurring_our_share_minor: customer.recurring_our_share_minor,
    our_cost_minor: customer.our_cost_minor,
    contribution_minor: customer.contribution_minor,
    margin_pct: customer.margin_pct,
  };
}

/** A month a customer had nothing in: zero billed, the rest unknown. */
function emptyPoint(period: string): MarginTrendPoint {
  return {
    period,
    recurring_our_share_minor: 0,
    our_cost_minor: null,
    contribution_minor: null,
    margin_pct: null,
  };
}

/** Why a history and a month report cannot be put together. */
export class MarginHistoryMismatch extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MarginHistoryMismatch";
  }
}

/**
 * The trend the server would have answered for `history`'s first month to the
 * report's month, under the same filters.
 *
 * Refuses (throws `MarginHistoryMismatch`) when the two do not describe one
 * question: another currency or partner scope, or a history that does not end
 * the month before the report's. Gluing those together would draw a chart
 * whose months were measured differently.
 */
export function withReportMonth(
  history: MarginTrendReport,
  report: MarginReport,
): MarginTrendReport {
  const period = report.params.period;
  if (history.params.to_month !== shiftPeriod(period, -1)) {
    throw new MarginHistoryMismatch(
      `The history ends in ${history.params.to_month}, not the month before ${period}`,
    );
  }
  if (history.currency !== report.currency) {
    throw new MarginHistoryMismatch(
      `The history is in ${history.currency} and the month in ${report.currency}`,
    );
  }
  if (
    history.params.partner_id !== report.params.partner_id ||
    (history.params.partner_id !== null && history.params.scope !== report.params.scope)
  ) {
    throw new MarginHistoryMismatch("The history and the month cover different partners");
  }

  const earlierPeriods = history.book.map((point) => point.period);
  const thisMonth = new Map(report.customers.map((customer) => [customer.deal_id, customer]));

  const customers: MarginTrendCustomer[] = history.customers.map((line) => {
    const customer = thisMonth.get(line.deal_id);
    return {
      deal_id: line.deal_id,
      // The server keeps the name of the last month the customer appears in.
      customer_name: customer?.customer_name ?? line.customer_name,
      points: [
        ...line.points,
        customer === undefined ? emptyPoint(period) : customerPoint(period, customer),
      ],
    };
  });
  const known = new Set(history.customers.map((line) => line.deal_id));
  for (const customer of report.customers) {
    if (known.has(customer.deal_id)) continue;
    known.add(customer.deal_id);
    customers.push({
      deal_id: customer.deal_id,
      customer_name: customer.customer_name,
      points: [...earlierPeriods.map(emptyPoint), customerPoint(period, customer)],
    });
  }

  return {
    currency: history.currency,
    generated_at: report.generated_at,
    params: { ...history.params, to_month: period },
    book: [...history.book, bookPoint(report)],
    customers,
  };
}

/**
 * The twelve points one customer's chart draws, or undefined while the history
 * is not there (or does not fit the month, which the chart then leaves out
 * rather than drawing wrong).
 */
export function customerHistory(
  history: MarginTrendReport | undefined,
  report: MarginReport,
): ReadonlyMap<string, MarginTrendPoint[]> | undefined {
  if (history === undefined) return undefined;
  let trend: MarginTrendReport;
  try {
    trend = withReportMonth(history, report);
  } catch (error) {
    if (error instanceof MarginHistoryMismatch) return undefined;
    throw error;
  }
  return new Map(trend.customers.map((line) => [line.deal_id, line.points]));
}
